import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';

// ---- CAUSE COPY (plain-English, spec) --------------------------------------
const CAUSE_COPY = {
  'Delivered order absent from payout': {
    slug: 'delivered-absent',
    display: 'Delivered orders that were never paid',
    why: 'You fulfilled these orders and the platform confirmed delivery, but the settlement report shows no payout line — the money was owed under your commission agreement but never wired.',
  },
  'Ads / promo recovery not agreed': {
    slug: 'promo-not-agreed',
    display: 'Promotional discounts you never agreed to',
    why: 'Your commercial terms mark ads/promo recovery as NOT AGREED. Every rupee deducted under this head is disputable in full.',
  },
  'Commission charged above contracted rate': {
    slug: 'commission-excess',
    display: 'Commission charged above your agreed rate',
    why: 'Your contracted commission is fixed — any deduction above that (plus the GST on the excess) is recoverable.',
  },
  'TDS withheld at 1% instead of 0.1%': {
    slug: 'tds-wrong-rate',
    display: 'TDS deducted at the wrong rate',
    why: 'Section 194-O of the Income Tax Act rate is 0.1%. Any excess withholding is recoverable from the platform.',
  },
  'Card settlement short of billed minus MDR': {
    slug: 'card-short',
    display: 'Card settlements short of the billed amount',
    why: 'Card settlements carry no order-level reference, so this is a day-level finding — a limitation of card settlement data, not of the analysis.',
  },
};

const STATE_PILL = {
  matched: { bg: '#2F5D48', ink: '#F4F0E6', label: 'Matched' },
  short: { bg: '#B06A2C', ink: '#FBF8F0', label: 'Short' },
  over: { bg: '#6E5334', ink: '#FBF8F0', label: 'Over' },
  pending: { bg: '#8B8377', ink: '#FBF8F0', label: 'Pending' },
  missing: { bg: '#6b3a2c', ink: '#FBF8F0', label: 'Missing' },
  unexplained: { bg: '#6E5A85', ink: '#FBF8F0', label: 'Unexplained' },
  // When the bank statement for the period is not on file, every UTR
  // trivially has actual=null so the engine returns state="missing". We
  // relabel these to "Unverified" in the UI so the operator never claims
  // "money did not arrive" without the bank data to prove it.
  unverified: { bg: '#8B8377', ink: '#FBF8F0', label: 'Unverified' },
};

const fmt = n => (n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);
const fmt2 = n => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const iso = d => d.toISOString().slice(0, 10);

// ---- DATE HELPERS ---------------------------------------------------------
// "Fully reconcilable" = whole month whose settlement window has closed
// (per spec: ≥15 days after month-end). Default period spans the last full
// such month.
const SETTLE_LAG_DAYS = 15;

function defaultPeriod(today = new Date()) {
  // Walk back month-by-month from today until we find a month whose end date
  // is ≥ SETTLE_LAG_DAYS days in the past.
  let y = today.getFullYear();
  let m = today.getMonth();      // 0-indexed
  for (let i = 0; i < 6; i++) {  // try up to 6 months back — always finds one
    // Candidate: the month that ended before this cursor.
    // If cursor is (2026-02), candidate is 2026-01. Its last day: new Date(y, m, 0).
    const candLastDay = new Date(y, m, 0);       // day 0 of month m => last day of month m-1
    const days = Math.floor((today - candLastDay) / 86_400_000);
    if (days >= SETTLE_LAG_DAYS) {
      const first = new Date(candLastDay.getFullYear(), candLastDay.getMonth(), 1);
      return { start: iso(first), end: iso(candLastDay) };
    }
    // Step back one month
    m -= 1;
    if (m < 0) { m = 11; y -= 1; }
  }
  // Fallback (should never hit): a month a year ago
  const last = new Date(today.getFullYear(), today.getMonth() - 12, 0);
  const first = new Date(last.getFullYear(), last.getMonth(), 1);
  return { start: iso(first), end: iso(last) };
}

function presetPeriods(today = new Date()) {
  // Presets are always full months, most recent first.
  const out = [];
  const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
  for (let i = 1; i <= 6; i++) {
    const first = new Date(cursor.getFullYear(), cursor.getMonth() - i, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    const label = first.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    out.push({ label, start: iso(first), end: iso(last) });
  }
  return out;
}

function isSettlingWindow(endIso, today = new Date()) {
  // True if the end date is within SETTLE_LAG_DAYS of today (period not fully settled)
  const end = new Date(endIso);
  const days = Math.floor((today - end) / 86_400_000);
  return days < SETTLE_LAG_DAYS;
}

// ---- COMPONENT ------------------------------------------------------------

export default function Reconciliation() {
  const { selectedClient, API } = useClient();
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const [period, setPeriod] = useState(defaultPeriod());
  const [coverage, setCoverage] = useState(null);
  const [aggregator, setAggregator] = useState(null);
  const [card, setCard] = useState(null);
  const [upi, setUpi] = useState(null);
  const [cash, setCash] = useState(null);
  const [books, setBooks] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showWorking, setShowWorking] = useState(isOperator);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // null | 'all' | cause-slug

  useEffect(() => {
    if (!selectedClient) return;
    (async () => {
      setLoading(true);
      const cid = selectedClient.id;
      const q = `?start=${period.start}&end=${period.end}`;
      try {
        const [c, a, ch, uh, kh, bv] = await Promise.all([
          axios.get(`${API}/recon/${cid}/coverage${q}`),
          axios.get(`${API}/recon/${cid}/aggregator${q}`),
          axios.get(`${API}/recon/${cid}/hygiene/card${q}`),
          axios.get(`${API}/recon/${cid}/hygiene/upi${q}`),
          axios.get(`${API}/recon/${cid}/hygiene/cash${q}`),
          axios.get(`${API}/recon/${cid}/books-variance${q}`),
        ]);
        setCoverage(c.data);
        setAggregator(a.data);
        setCard(ch.data);
        setUpi(uh.data);
        setBooks(bv.data);
        setCash(kh.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [selectedClient, API, period]);

  const causesRolled = useMemo(() => {
    if (!aggregator?.findings) return [];
    const byC = {};
    for (const f of aggregator.findings) {
      byC[f.cause] = byC[f.cause] || { cause: f.cause, count: 0, sum: 0, orders: [], platforms: {} };
      byC[f.cause].count++;
      byC[f.cause].sum += f.amount;
      byC[f.cause].orders.push(f);
      byC[f.cause].platforms[f.platform] = (byC[f.cause].platforms[f.platform] || 0) + f.amount;
    }
    return Object.values(byC).sort((a, b) => b.sum - a.sum);
  }, [aggregator]);

  const totalOwed = coverage?.findings_total_rupees || 0;
  const earliestDispute = useMemo(() => {
    if (!aggregator?.findings?.length) return null;
    const ds = aggregator.findings.map(f => f.dispute_by).filter(Boolean).sort();
    return ds[0] || null;
  }, [aggregator]);
  const daysLeft = earliestDispute
    ? Math.max(0, Math.round((new Date(earliestDispute) - new Date()) / 86400000)) : null;

  // Evidence chain sample from the highest-value cause's first order
  const evidenceSample = useMemo(() => {
    if (!causesRolled.length) return null;
    return causesRolled[0].orders?.[0] || null;
  }, [causesRolled]);

  // Coverage bar segments
  const covData = coverage?.coverage;
  const segments = covData ? [
    { key: 'matched', label: 'Reconciled', color: '#2F5D48',
      count: covData.by_state_count?.matched || 0, rupees: covData.by_state_rupees?.matched || 0 },
    { key: 'short', label: 'Short-paid', color: '#B06A2C',
      count: covData.by_state_count?.short || 0, rupees: covData.by_state_rupees?.short || 0 },
    { key: 'pending', label: 'Still in transit', color: '#8B8377',
      count: covData.by_state_count?.pending || 0, rupees: covData.by_state_rupees?.pending || 0 },
    { key: 'unexplained', label: 'Not yet explained', color: '#6E5A85',
      count: covData.by_state_count?.unexplained || 0, rupees: covData.by_state_rupees?.unexplained || 0 },
  ] : [];
  const covTotal = segments.reduce((a, s) => a + s.count, 0) || 1;

  const settling = isSettlingWindow(period.end);
  const presets = useMemo(() => presetPeriods(), []);

  async function downloadExport(cause = null) {
    if (!selectedClient) return;
    const key = cause ? (CAUSE_COPY[cause]?.slug || 'cause') : 'all';
    setExporting(key);
    try {
      const cid = selectedClient.id;
      const params = new URLSearchParams({ start: period.start, end: period.end });
      if (cause) params.set('cause', cause);
      const res = await axios.get(`${API}/recon/${cid}/export?${params.toString()}`,
                                    { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      // Try to extract filename from Content-Disposition; fall back
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.href = url;
      a.download = m ? m[1] : `recon_${period.start}_to_${period.end}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('export failed', e);
      alert('Export failed. See console for details.');
    } finally {
      setExporting(null);
    }
  }

  if (!selectedClient) return <div className="p-8" style={{ color: '#211C16' }}>Select a client.</div>;

  return (
    <div style={{ background: '#F4F0E6', minHeight: '100vh', color: '#211C16', fontFamily: 'Manrope, sans-serif' }}
         data-testid="reconciliation-centre">
      <div className="max-w-6xl mx-auto px-8 py-10 space-y-10">

        {/* PERIOD SELECTOR */}
        <section data-testid="recon-period-selector"
                 style={{ background: '#FBF8F0', borderRadius: 10, padding: 20,
                          border: '1px solid #EDE5D3' }}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest mb-1.5"
                     style={{ color: '#6E5334' }}>Start date</label>
              <input type="date" value={period.start}
                     data-testid="recon-date-start"
                     onChange={e => setPeriod({ ...period, start: e.target.value })}
                     style={{ background: '#F4F0E6', border: '1px solid #EDE5D3',
                              borderRadius: 6, padding: '8px 10px', fontFamily: 'inherit',
                              color: '#211C16' }} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest mb-1.5"
                     style={{ color: '#6E5334' }}>End date</label>
              <input type="date" value={period.end}
                     data-testid="recon-date-end"
                     onChange={e => setPeriod({ ...period, end: e.target.value })}
                     style={{ background: '#F4F0E6', border: '1px solid #EDE5D3',
                              borderRadius: 6, padding: '8px 10px', fontFamily: 'inherit',
                              color: '#211C16' }} />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="recon-period-presets">
              {presets.map(p => (
                <button key={p.label}
                        data-testid={`recon-preset-${p.label.replace(' ', '-').toLowerCase()}`}
                        onClick={() => setPeriod({ start: p.start, end: p.end })}
                        style={{
                          background: period.start === p.start && period.end === p.end
                            ? '#2F5D48' : '#F4F0E6',
                          color: period.start === p.start && period.end === p.end
                            ? '#F4F0E6' : '#211C16',
                          border: '1px solid #EDE5D3', borderRadius: 999,
                          padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                        }}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="ml-auto">
              <button onClick={() => downloadExport()}
                      data-testid="recon-export-all-button"
                      disabled={exporting === 'all' || loading}
                      style={{
                        background: '#2F5D48', color: '#F4F0E6',
                        border: 'none', borderRadius: 6,
                        padding: '10px 18px', fontSize: 13, fontWeight: 500,
                        cursor: exporting === 'all' ? 'wait' : 'pointer',
                        opacity: exporting === 'all' ? 0.6 : 1,
                      }}>
                {exporting === 'all' ? 'Preparing…' : 'Export Excel (all sheets)'}
              </button>
            </div>
          </div>
          {settling && (
            <div className="mt-4 px-3 py-2 text-sm"
                 data-testid="recon-settling-warning"
                 style={{ background: '#F4E9D6', border: '1px solid #B06A2C',
                          borderRadius: 6, color: '#6E5334' }}>
              <strong>Still settling.</strong> This period ends within 15 days of today —
              aggregator UTRs and card/UPI credits are typically finalised
              ~15 days after the last transaction, so the numbers below will
              tighten as they clear. Consider selecting an earlier month for
              audit-grade figures.
            </div>
          )}
        </section>

        {/* HERO */}
        <section data-testid="recon-hero">
          <div className="text-xs uppercase tracking-widest" style={{ color: '#6E5334' }}>
            Reconciliation Centre · {period.start} → {period.end}
          </div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: '76px', lineHeight: 1, color: '#B06A2C',
                        fontFeatureSettings: '"tnum"', marginTop: 12 }}
               data-testid="recon-total-owed">
            {loading ? '…' : fmt(totalOwed)}
          </div>
          {/* Honest, cause-aware subtitle. When every finding is a
              commission over-charge we say so explicitly — nothing "did
              not arrive"; it was over-deducted. When bank data for the
              period is missing we tell the operator that the UTR chain
              below cannot be verified until a bank statement is uploaded. */}
          {(() => {
            const findings = aggregator?.findings || [];
            const n = findings.length;
            const causeSet = new Set(findings.map(f => f.cause));
            const soleCause = causeSet.size === 1 ? [...causeSet][0] : null;
            const bankMissing = aggregator && aggregator.has_bank_coverage === false;
            let line;
            if (n === 0) {
              line = 'No aggregator leakage found for this period. Settlement lines and contracted terms agree.';
            } else if (soleCause === 'Commission charged above contracted rate') {
              line = <><strong>{n}</strong> orders where the platform charged commission above your contracted rate. Every rupee traces to an order ID in the settlement file.</>;
            } else if (soleCause === 'Delivered order absent from payout') {
              line = <><strong>{n}</strong> delivered orders that never appeared on a settlement payout line.</>;
            } else if (soleCause) {
              line = <><strong>{n}</strong> orders flagged as: {soleCause}.</>;
            } else {
              line = <>Across <strong>{n}</strong> orders, deductions exceeded your contracted terms. Every rupee traces to an order ID in the settlement file.</>;
            }
            return (
              <>
                <p style={{ maxWidth: 620, marginTop: 16, fontSize: 15, lineHeight: 1.6 }}
                   data-testid="recon-hero-subtitle">
                  {line}
                </p>
                {bankMissing && (
                  <div className="mt-4 px-3 py-2 text-sm"
                       data-testid="recon-no-bank-data-banner"
                       style={{ background: '#F4E9D6', border: '1px solid #B06A2C',
                                borderRadius: 6, color: '#6E5334', maxWidth: 620 }}>
                    <strong>Bank statement not on file for this period.</strong>{' '}
                    UTR credits in the payout chain below cannot be verified until you
                    upload the bank statement — we show them as <em>Unverified</em>,
                    not as missing money.
                  </div>
                )}
              </>
            );
          })()}
          {daysLeft != null && (
            <div className="inline-flex items-center gap-2 mt-6 px-3 py-1.5 text-xs"
                 data-testid="dispute-countdown-chip"
                 style={{ background: '#FBF8F0', border: '1px solid #B06A2C', borderRadius: 999, color: '#6E5334' }}>
              Dispute window closes <strong>{earliestDispute}</strong>
              · <span style={{ color: '#B06A2C' }}>{daysLeft} days left</span>
            </div>
          )}
        </section>

        {/* SETTLEMENT PIPELINE — aggregator only; card/UPI/cash are checked
            at MONTH level below (per grain change iter-106) and never contribute
            to the recoverable headline above. */}
        <section data-testid="settlement-pipeline">
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, marginBottom: 18 }}>Aggregator payouts</h2>
          {[
            { name: 'Aggregator payouts (UTR-chain)', owed: aggregator?.buckets?.reduce((a, b) => a + b.expected, 0) || 0,
              received: aggregator?.buckets?.reduce((a, b) => a + (b.actual || 0), 0) || 0,
              short: aggregator?.findings?.reduce((a, f) => a + f.amount, 0) || 0 },
          ].map(s => {
            const total = Math.max(1, s.owed);
            const shortPct = Math.min(100, (s.short / total) * 100);
            const receivedPct = 100 - shortPct;
            // When bank data for the period is not on file, we cannot honestly
            // claim any of the owed amount was "received" — we swap in a
            // truthful "not yet verifiable" label instead of a fabricated ₹0.
            const bankMissing = aggregator && aggregator.has_bank_coverage === false;
            return (
              <div key={s.name} className="mb-5" data-testid="stream-aggregator">
                <div className="flex justify-between text-sm mb-1.5">
                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                  <span style={{ color: '#6E5334' }}>
                    owed {fmt(s.owed)}
                    {bankMissing
                      ? ' · bank statement not on file'
                      : <> · received {fmt(s.received)}</>}
                    {s.short > 1 ? ` · recoverable ${fmt(s.short)}` : ' · all matched'}
                  </span>
                </div>
                <div className="h-2.5 rounded-full overflow-hidden flex" style={{ background: '#EDE5D3' }}>
                  {bankMissing ? (
                    <div style={{ width: '100%',
                                  background: 'repeating-linear-gradient(45deg,#EDE5D3 0 6px,#DDD3BF 6px 12px)' }}></div>
                  ) : (
                    <>
                      <div style={{ width: `${receivedPct}%`, background: '#2F5D48' }}></div>
                      <div style={{ width: `${shortPct}%`, background: '#B06A2C' }}></div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          <p className="text-xs mt-2" style={{ color: '#6E5334', lineHeight: 1.6, maxWidth: 640 }}>
            Aggregator payouts reconcile to the order via the UTR chain (weekly settlement).
            Card / UPI / cash are checked at month level below — they settle in aggregate
            with no order reference on the bank line, so per-order attribution is not possible.
          </p>
        </section>

        {/* CARD / UPI / CASH — MONTHLY HYGIENE. Never recoverable. */}
        {(card || upi || cash) && (
          <section data-testid="tender-hygiene-section">
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, marginBottom: 8 }}>
              Card, UPI &amp; cash · monthly totals check
            </h2>
            <p className="text-xs mb-4" style={{ color: '#6E5334', maxWidth: 720, lineHeight: 1.6 }}>
              These are totals sanity-checks, not recoverable claims. A small gap is
              within the ordinary settlement float; a persistent large gap is flagged
              for human review.
            </p>
            {[
              { data: card, label: 'Card', testid: 'hygiene-card' },
              { data: upi,  label: 'UPI',  testid: 'hygiene-upi' },
              { data: cash, label: 'Cash', testid: 'hygiene-cash' },
            ].map(({ data, label, testid }) => data && (
              <div key={label} className="mb-5" data-testid={testid}>
                <div className="text-sm mb-2" style={{ fontWeight: 500 }}>{label}</div>
                <div className="overflow-x-auto" style={{ background: '#FBF8F0', borderRadius: 8, padding: 12 }}>
                  <table className="w-full text-sm" style={{ fontFeatureSettings: '"tnum"' }}>
                    <thead style={{ color: '#6E5334', fontSize: 12, textAlign: 'left' }}>
                      <tr>
                        <th className="pb-1.5">Month</th>
                        <th className="pb-1.5 text-right">POS billed</th>
                        <th className="pb-1.5 text-right">Expected</th>
                        <th className="pb-1.5 text-right">Bank received</th>
                        <th className="pb-1.5 text-right">Gap</th>
                        <th className="pb-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.rows || []).map(r => (
                        <tr key={r.month} style={{ borderTop: '1px solid #EDE5D3' }}>
                          <td className="py-1.5">{r.month}</td>
                          <td className="py-1.5 text-right">{fmt(r.billed)}</td>
                          <td className="py-1.5 text-right">{fmt(r.expected)}</td>
                          <td className="py-1.5 text-right">{fmt(r.actual)}</td>
                          <td className="py-1.5 text-right" style={{ color: Math.abs(r.gap) > 500 ? '#B06A2C' : '#6E5334' }}>{fmt(r.gap)}</td>
                          <td className="py-1.5 text-xs" style={{ color: r.status === 'flag_for_review' ? '#B06A2C' : '#2F5D48' }}>
                            {r.status === 'within_range' ? 'within expected range'
                              : r.status === 'flag_for_review' ? 'flag for review'
                              : 'no activity'}
                          </td>
                        </tr>
                      ))}
                      {(data.rows || []).length === 0 && (
                        <tr><td colSpan={6} className="py-2 text-xs" style={{ color: '#6E5334' }}>No {label.toLowerCase()} activity in the period.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* CAUSES */}
        <section data-testid="causes-section">
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, marginBottom: 18 }}>Where the money went</h2>
          <div className="space-y-3">
            {causesRolled.map(c => {
              const copy = CAUSE_COPY[c.cause] || { display: c.cause, why: '', slug: 'other' };
              const isOpen = expanded[c.cause];
              const exportKey = copy.slug;
              return (
                <div key={c.cause}
                     data-testid={`cause-card-${copy.slug}`}
                     style={{ background: '#FBF8F0', borderRadius: 10, padding: 20, border: '1px solid #EDE5D3' }}>
                  <div className="flex items-center justify-between gap-4">
                    <button onClick={() => setExpanded({ ...expanded, [c.cause]: !isOpen })}
                            data-testid={`cause-toggle-${copy.slug}`}
                            className="flex-1 flex items-center justify-between text-left gap-4">
                      <div>
                        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 18 }}>{copy.display}</div>
                        <div className="text-xs mt-1" style={{ color: '#6E5334' }}>
                          {c.count} orders · {Object.entries(c.platforms).map(([p, v]) => `${p} ${fmt(v)}`).join(' · ')}
                        </div>
                      </div>
                      <div style={{ fontFamily: 'Fraunces, serif', fontSize: 26, color: '#B06A2C',
                                     fontFeatureSettings: '"tnum"' }}>{fmt(c.sum)}</div>
                    </button>
                    <button onClick={() => downloadExport(c.cause)}
                            data-testid={`cause-export-${copy.slug}`}
                            disabled={exporting === exportKey}
                            title="Download 4-sheet workbook filtered to this cause"
                            style={{
                              background: '#F4F0E6', color: '#2F5D48',
                              border: '1px solid #2F5D48', borderRadius: 6,
                              padding: '6px 12px', fontSize: 12, fontWeight: 500,
                              cursor: exporting === exportKey ? 'wait' : 'pointer',
                              opacity: exporting === exportKey ? 0.6 : 1,
                              whiteSpace: 'nowrap',
                            }}>
                      {exporting === exportKey ? 'Preparing…' : 'Excel'}
                    </button>
                  </div>
                  {isOpen && (
                    <div className="mt-5 pt-5 border-t" style={{ borderColor: '#EDE5D3' }}>
                      <p className="text-sm mb-4" style={{ color: '#3d332a' }}>{copy.why}</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" style={{ fontFeatureSettings: '"tnum"' }}>
                          <thead style={{ color: '#6E5334', fontSize: 12, textAlign: 'left' }}>
                            <tr><th className="pb-2">Order</th><th className="pb-2">Date</th>
                                <th className="pb-2">Platform</th><th className="pb-2 text-right">Short by</th></tr>
                          </thead>
                          <tbody>
                            {c.orders.slice(0, 10).map(o => (
                              <tr key={o.order_id} style={{ borderTop: '1px solid #EDE5D3' }}>
                                <td className="py-2">{o.order_id}</td>
                                <td className="py-2">{o.settlement_date}</td>
                                <td className="py-2">{o.platform}</td>
                                <td className="py-2 text-right" style={{ color: '#B06A2C', fontWeight: 500 }}>{fmt(o.amount)}</td>
                              </tr>
                            ))}
                            {c.orders.length > 10 && (
                              <tr><td colSpan={4} className="pt-3 text-xs" style={{ color: '#6E5334' }}>
                                +{c.orders.length - 10} more orders in this cause
                              </td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* EVIDENCE CHAIN */}
        {evidenceSample && (
          <section data-testid="evidence-chain"
                   style={{ background: '#FBF8F0', borderRadius: 10, padding: 24,
                            border: '2px dashed #2F5D48' }}>
            <div className="text-xs uppercase tracking-widest mb-3" style={{ color: '#2F5D48' }}>
              Custody trail — one order, four sources
            </div>
            <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 20, marginBottom: 16 }}>
              How we prove {evidenceSample.order_id} is recoverable
            </h3>
            <div className="grid grid-cols-4 gap-3 items-stretch">
              {[
                { label: 'Your POS', body: `Bill for ${evidenceSample.order_id}\nSettlement date ${evidenceSample.settlement_date}` },
                { label: 'Platform settlement', body: `${evidenceSample.platform} report line\nDeduction under "${CAUSE_COPY[evidenceSample.cause]?.display || evidenceSample.cause}"` },
                { label: 'Payout UTR', body: `UTR ${evidenceSample.utr}\nsettled ${evidenceSample.settlement_date}` },
                { label: 'Your bank', body: `${evidenceSample.citation?.bank_txn_ref ? 'Bank credit matched' : 'Bank line resolved'}\nreconciles to platform total` },
              ].map((s, i) => (
                <div key={i} style={{ background: '#F4F0E6', padding: 14, borderRadius: 6, position: 'relative' }}>
                  <div className="text-xs mb-1.5" style={{ color: '#2F5D48', fontWeight: 600 }}>{s.label}</div>
                  <div className="text-sm" style={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>{s.body}</div>
                  {i < 3 && (
                    <div className="hidden md:block" style={{ position: 'absolute', right: -12, top: '50%',
                                                               color: '#2F5D48', fontSize: 20 }}>→</div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs" style={{ color: '#6E5334' }}>
              Dispute-by <strong>{evidenceSample.dispute_by}</strong> · every finding in this centre carries the same chain.
            </p>
          </section>
        )}

        {/* COVERAGE */}
        <section data-testid="coverage-bar">
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, marginBottom: 18 }}>Coverage</h2>
          <div className="h-3 rounded-full overflow-hidden flex" style={{ background: '#EDE5D3' }}>
            {segments.map(s => (
              <div key={s.key} style={{ width: `${(s.count / covTotal) * 100}%`, background: s.color }}></div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {segments.map(s => (
              <div key={s.key}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color, display: 'inline-block' }}></span>
                  <span style={{ color: '#6E5334', fontSize: 12 }}>{s.label}</span>
                </div>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20 }}>{fmt(s.rupees)}</div>
                <div style={{ color: '#6E5334', fontSize: 11 }}>{s.count} buckets</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs" style={{ color: '#6E5334', maxWidth: 720, lineHeight: 1.6 }}>
            Aggregator reconciled to the order via UTR (weekly settlement) ·
            card / UPI / cash checked at month level · books compared to POS
            separately. No single blended figure — each stream has a different
            grain and a different question.
          </p>
        </section>

        {/* BOOKS VARIANCE — SEPARATE STREAM, NEVER RECOVERABLE.
            A POS↔Tally gap is an internal accounting discrepancy. It is
            walled off from the Recoverable headline, causes, and findings
            on purpose — same treatment as mandi negotiation headroom. */}
        {books && (
          <section data-testid="books-variance-section"
                   style={{ background: '#FBF8F0', borderRadius: 10, padding: 24,
                            border: '1px solid #EDE5D3', marginTop: 8 }}>
            <div className="text-xs uppercase tracking-widest" style={{ color: '#6E5334' }}>
              Books variance · not recoverable
            </div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, marginTop: 4, marginBottom: 16 }}>
              POS recorded vs Tally booked
            </h2>
            {books.status === 'no_tally' ? (
              <div className="text-sm" data-testid="books-variance-no-data"
                   style={{ color: '#6E5334' }}>{books.status_message}</div>
            ) : (
              <>
                {books.status_message && (
                  <div className="text-xs mb-4 px-3 py-2"
                       data-testid="books-variance-status-message"
                       style={{ background: '#F4E9D6', border: '1px solid #B06A2C',
                                borderRadius: 6, color: '#6E5334' }}>
                    {books.status_message}
                  </div>
                )}
                <p className="text-sm" style={{ maxWidth: 720, lineHeight: 1.6 }}
                   data-testid="books-variance-headline">
                  POS recorded <strong>{fmt(books.pos_total)}</strong>; Tally
                  books <strong>{fmt(books.tally_total)}</strong>. Difference
                  <strong> {fmt(books.difference)}</strong> ({books.difference_pct.toFixed(1)}%
                  of POS){books.concentration_hint === 'cash'
                    ? ', concentrated on cash days.'
                    : books.concentration_hint === 'card_upi'
                    ? ', concentrated on card/UPI days.'
                    : '.'}
                </p>
                <p className="text-xs mt-3" style={{ color: '#6E5334', maxWidth: 720, lineHeight: 1.5 }}>
                  This is an internal books comparison — accounting error,
                  timing difference, or something else entirely. It is not
                  money a counterparty owes and is not counted in Recoverable
                  above.
                </p>

                {books.by_payment_mode?.length > 0 && (
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#6E5334' }}>
                      POS sales by payment mode
                    </div>
                    <table className="w-full text-sm" style={{ fontFeatureSettings: '"tnum"' }}
                           data-testid="books-variance-modes">
                      <thead style={{ color: '#6E5334', fontSize: 12 }}>
                        <tr><th className="text-left pb-1.5">Mode</th>
                            <th className="text-right pb-1.5">POS</th>
                            <th className="text-right pb-1.5">Share</th></tr>
                      </thead>
                      <tbody>
                        {books.by_payment_mode.map(r => (
                          <tr key={r.mode} style={{ borderTop: '1px solid #EDE5D3' }}>
                            <td className="py-1.5">{r.mode}</td>
                            <td className="py-1.5 text-right">{fmt(r.pos_total)}</td>
                            <td className="py-1.5 text-right" style={{ color: '#6E5334' }}>{r.share_pct.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {books.top_day_gaps?.length > 0 && (
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#6E5334' }}>
                      Largest daily gaps
                    </div>
                    <table className="w-full text-sm" style={{ fontFeatureSettings: '"tnum"' }}
                           data-testid="books-variance-top-days">
                      <thead style={{ color: '#6E5334', fontSize: 12 }}>
                        <tr><th className="text-left pb-1.5">Date</th>
                            <th className="text-right pb-1.5">POS</th>
                            <th className="text-right pb-1.5">Tally</th>
                            <th className="text-right pb-1.5">Difference</th></tr>
                      </thead>
                      <tbody>
                        {books.top_day_gaps.map(r => (
                          <tr key={r.date} style={{ borderTop: '1px solid #EDE5D3' }}>
                            <td className="py-1.5">{r.date}</td>
                            <td className="py-1.5 text-right">{fmt(r.pos)}</td>
                            <td className="py-1.5 text-right">{fmt(r.tally)}</td>
                            <td className="py-1.5 text-right" style={{ color: r.diff >= 0 ? '#B06A2C' : '#2F5D48' }}>{fmt(r.diff)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* SHOW THE WORKING — aggregator UTR chain only.
            The old per-day tie-out for card/UPI was removed in iter-106:
            those tenders settle in aggregate and cannot be tied per-day. */}
        <section data-testid="working-section">
          <button onClick={() => setShowWorking(!showWorking)}
                  data-testid="toggle-working-button"
                  className="text-sm underline" style={{ color: '#6E5334' }}>
            {showWorking ? 'Hide' : 'Show'} the working — aggregator UTR chain
          </button>
          {showWorking && (
            <div className="mt-4 overflow-x-auto" data-testid="working-table"
                 style={{ background: '#FBF8F0', borderRadius: 8, padding: 16 }}>
              <table className="w-full text-sm" style={{ fontFeatureSettings: '"tnum"' }}>
                <thead style={{ color: '#6E5334', fontSize: 12, textAlign: 'left' }}>
                  <tr>
                    <th className="pb-2">Platform</th><th className="pb-2">UTR</th>
                    <th className="pb-2">Settlement date</th>
                    <th className="pb-2 text-right">Expected</th>
                    <th className="pb-2 text-right">Received</th>
                    <th className="pb-2 text-right">Diff</th>
                    <th className="pb-2">State</th>
                  </tr>
                </thead>
                <tbody>
                  {(aggregator?.buckets || []).slice(0, 60).map(b => {
                    // When bank data is absent for the whole period we
                    // relabel state="missing" to "unverified" so the row
                    // never accuses the platform without the bank
                    // evidence to back it up.
                    const bankMissing = aggregator && aggregator.has_bank_coverage === false;
                    const effectiveState = (bankMissing && (b.state === 'missing' || b.state === 'pending'))
                      ? 'unverified' : b.state;
                    const pill = STATE_PILL[effectiveState] || STATE_PILL.unexplained;
                    return (
                      <tr key={b.utr} style={{ borderTop: '1px solid #EDE5D3' }}>
                        <td className="py-2">{b.platform}</td>
                        <td className="py-2 text-xs">{b.utr}</td>
                        <td className="py-2">{b.settlement_date}</td>
                        <td className="py-2 text-right">{fmt2(b.expected)}</td>
                        <td className="py-2 text-right">{fmt2(b.actual)}</td>
                        <td className="py-2 text-right" style={{ color: b.variance && b.variance < 0 ? '#B06A2C' : '#2F5D48' }}>
                          {b.variance != null ? fmt2(b.variance) : '—'}
                        </td>
                        <td className="py-2">
                          <span className="px-2 py-0.5 text-xs rounded"
                                style={{ background: pill.bg, color: pill.ink, fontWeight: 500 }}>{pill.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-4 text-xs" style={{ color: '#6E5334' }}>
                <strong>Matched</strong> — payout equalled the aggregator&apos;s contractual expected.
                <strong> Short</strong> — payout below expected (recoverable finding above).
                <strong> Pending</strong> — inside the settlement window, not yet due.
                <strong> Missing</strong> — window elapsed with no bank credit for this UTR.
                <strong> Unverified</strong> — bank statement not on file, cannot check credit either way.
              </p>
            </div>
          )}
        </section>

      </div>
    </div>
  );
}
